var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-param-reassign */
/* eslint-disable class-methods-use-this */
import { buildPointLightGrid, disposeGeometryRecursively, UVMapOffsetShape, instantlyPlayAnimation, AnimationDirection, UVMapExtrudedShape } from "@rendering/vortex-core";
import { Group, Mesh, ExtrudeGeometry, Vector3, ShapeGeometry, Box3, Vector2 } from "three";
import { convertToShape, preserve, mirror, convertPathToShape } from "../common/pathHelper";
import { FoldingAnimation } from "./animations";
import { getSection } from "./helpers";
const CURVE_SEGMENTS = 15;
const EPSILON = 0.0001;
// Separate content and body layers by 20 micrometers
const LAYER_OFFSET = 0.02;
export class NFoldProduct2 {
  constructor(config) {
    var _a, _b, _c, _d, _e, _f;
    Object.defineProperty(this, "type", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: "nfold"
    });
    Object.defineProperty(this, "config", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "primarySectionSet", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "surfaceMapping", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "sectionGroupMap", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "initializedGeometry", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "physicalSize", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "currentState", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "productStates", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: void 0
    });
    Object.defineProperty(this, "currentLayerOffset", {
      enumerable: true,
      configurable: true,
      writable: true,
      value: LAYER_OFFSET
    });
    this.config = config;
    // eslint-disable-next-line prefer-destructuring
    this.primarySectionSet = config.sectionSets[0];
    this.surfaceMapping = {};
    this.sectionGroupMap = {};
    const {
      fullBleed
    } = this.primarySectionSet;
    const rightTrim = fullBleed.width - this.primarySectionSet.trim.bottomRightX;
    const bottomTrim = fullBleed.height - this.primarySectionSet.trim.bottomRightY;
    this.physicalSize = {
      width: fullBleed.width - (this.primarySectionSet.trim.topLeftX + rightTrim),
      height: fullBleed.height - (this.primarySectionSet.trim.topLeftY + bottomTrim),
      depth: this.primarySectionSet.thickness
    };
    this.productStates = [{
      id: "Unfolded"
    }];
    // Build the product states for fold sequences
    for (const foldSequence of this.primarySectionSet.foldSequences) {
      // Ensure dynamic focal points can be generated
      const state = {
        id: foldSequence.id,
        allowGeneratedFocalPoints: true
      };
      if (foldSequence.dependencyId) {
        // Open
        state.dependency = {
          stateId: foldSequence.dependencyId,
          animationId: foldSequence.id,
          animationDirection: AnimationDirection.forward,
          animationNames: {
            forward: (_a = foldSequence.animationNames) === null || _a === void 0 ? void 0 : _a.forward,
            backward: (_b = foldSequence.animationNames) === null || _b === void 0 ? void 0 : _b.backward
          }
        };
      } else {
        // Folded
        state.dependency = {
          stateId: "Unfolded",
          animationId: foldSequence.id,
          animationDirection: AnimationDirection.forward,
          animationNames: {
            forward: (_d = (_c = foldSequence.animationNames) === null || _c === void 0 ? void 0 : _c.forward) !== null && _d !== void 0 ? _d : "Unfold",
            backward: (_f = (_e = foldSequence.animationNames) === null || _e === void 0 ? void 0 : _e.backward) !== null && _f !== void 0 ? _f : "Fold"
          }
        };
      }
      this.productStates.push(state);
    }
    const foldedState = this.productStates.find(state => state.id === "Folded");
    if (!foldedState) {
      throw new Error("Must have 'Folded' as a fold sequence");
    }
    this.currentState = foldedState;
  }
  // Builds and textures a section front/body/back
  buildSectionMeshes(section) {
    const maxSize = {
      width: this.physicalSize.width,
      height: this.physicalSize.height
    };
    const sectionMeshes = [];
    sectionMeshes.push(...this.buildFrontSection(section, maxSize));
    sectionMeshes.push(...this.buildSectionBody(section, maxSize));
    sectionMeshes.push(...this.buildSectionBack(section, maxSize));
    return sectionMeshes;
  }
  buildFrontSection(section, maxSize) {
    const frontGeometry = new ShapeGeometry(convertToShape(section.path, maxSize, {
      x: preserve,
      y: mirror
    }, {
      x: preserve,
      y: mirror
    }), CURVE_SEGMENTS);
    UVMapOffsetShape(frontGeometry, this.physicalSize.width, this.physicalSize.height, this.getContentOffset(section.frontSide));
    const meshes = [];
    // Create the front geometry slightly in front of the body
    const frontMesh = new Mesh(frontGeometry);
    frontMesh.uuid = "".concat(section.id, ":Front");
    frontMesh.position.z = this.primarySectionSet.thickness + LAYER_OFFSET;
    meshes.push(frontMesh);
    // Map the front surface
    this.surfaceMapping[frontMesh.uuid] = {
      substrate: section.frontSide.substrate,
      fullBleed: this.primarySectionSet.fullBleed,
      trim: this.primarySectionSet.trim,
      page: section.frontSide.page,
      focalPoint: section.frontSide.focalPoint,
      finishes: section.frontSide.finishes
    };
    // Create premium finishes if they exist
    if (section.frontSide.finishes) {
      let finishOffset = LAYER_OFFSET * 2;
      for (const finish of section.frontSide.finishes) {
        // Create the finish geometry slightly in front of the front geometry
        const finishMesh = new Mesh(frontGeometry);
        finishMesh.uuid = "".concat(section.id, ":Front-").concat(finish.name);
        finishMesh.position.z = this.primarySectionSet.thickness + finishOffset;
        meshes.push(finishMesh);
        // Keep incrementing the layer offset for multiple finishes
        finishOffset += LAYER_OFFSET;
      }
    }
    return meshes;
  }
  buildSectionBody(section, maxSize) {
    const bodyMeshes = [];
    // Ignore rendering the body geometry folded product has a low thickness
    if (this.primarySectionSet.thickness > EPSILON) {
      // Build extrude settings based on thickness for the body mesh
      const extrudeSettings = {
        bevelEnabled: false,
        depth: this.primarySectionSet.thickness,
        curveSegments: CURVE_SEGMENTS
      };
      const bodyGeometry = new ExtrudeGeometry(convertToShape(section.path, maxSize, {
        x: preserve,
        y: mirror
      }, {
        x: preserve,
        y: mirror
      }), extrudeSettings);
      UVMapExtrudedShape(bodyGeometry, this.physicalSize);
      const bodyMesh = new Mesh(bodyGeometry);
      bodyMesh.uuid = "".concat(section.id, ":Body");
      // Map the body surface
      this.surfaceMapping[bodyMesh.uuid] = {
        substrate: section.bodySubstrate,
        fullBleed: this.primarySectionSet.fullBleed
      };
      bodyMeshes.push(bodyMesh);
    }
    // If the section has any fills include them as meshes
    if (section.path.fills && section.fillSubstrates) {
      // Build extrude settings based on thickness for the body mesh
      const extrudeSettings = {
        bevelEnabled: false,
        depth: LAYER_OFFSET,
        curveSegments: CURVE_SEGMENTS
      };
      for (let i = 0; i < section.path.fills.length; i++) {
        const fillPath = section.path.fills[i];
        const fillGeometry = new ExtrudeGeometry(convertPathToShape(fillPath, maxSize, preserve, mirror), extrudeSettings);
        UVMapExtrudedShape(fillGeometry, this.physicalSize);
        const fillMesh = new Mesh(fillGeometry);
        fillMesh.uuid = "".concat(section.id, ":Body Fill:").concat(i);
        // Map the fill surface
        this.surfaceMapping[fillMesh.uuid] = {
          substrate: section.fillSubstrates[i],
          fullBleed: this.primarySectionSet.fullBleed
        };
        bodyMeshes.push(fillMesh);
      }
    }
    return bodyMeshes;
  }
  buildSectionBack(section, maxSize) {
    const backGeometry = new ShapeGeometry(convertToShape(section.path, maxSize, {
      x: mirror,
      y: mirror
    }, {
      x: mirror,
      y: mirror
    }), CURVE_SEGMENTS);
    UVMapOffsetShape(backGeometry, this.physicalSize.width, this.physicalSize.height, this.getContentOffset(section.backSide));
    const meshes = [];
    const backMesh = new Mesh(backGeometry);
    backMesh.uuid = "".concat(section.id, ":Back");
    meshes.push(backMesh);
    // Move the back mesh behind the body and rotate/mirror it
    backMesh.position.z = -LAYER_OFFSET;
    backMesh.position.x += this.physicalSize.width;
    backMesh.rotation.y = Math.PI;
    // Map the back surface
    this.surfaceMapping[backMesh.uuid] = {
      substrate: section.backSide.substrate,
      fullBleed: this.primarySectionSet.fullBleed,
      trim: this.primarySectionSet.trim,
      page: section.backSide.page,
      focalPoint: section.backSide.focalPoint,
      finishes: section.backSide.finishes
    };
    // Create premium finishes if they exist
    if (section.backSide.finishes) {
      let finishOffset = LAYER_OFFSET * 2;
      for (const finish of section.backSide.finishes) {
        // Create the finish geometry slightly in behind of the back geometry
        const finishMesh = new Mesh(backGeometry);
        finishMesh.uuid = "".concat(section.id, ":Back-").concat(finish.name);
        meshes.push(finishMesh);
        // Move the back mesh behind the body and rotate/mirror it
        finishMesh.position.z = -finishOffset;
        finishMesh.position.x += this.physicalSize.width;
        finishMesh.rotation.y = Math.PI;
        // Keep incrementing the layer offset for multiple finishes
        finishOffset += LAYER_OFFSET;
      }
    }
    return meshes;
  }
  // Builds a section group starting with a source section
  buildSectionGroup(section) {
    // Initialize and index a new section group
    const group = new Group();
    this.sectionGroupMap[section.id] = group;
    // Add the section mesh to its group
    const sectionMeshes = this.buildSectionMeshes(section);
    group.add(...sectionMeshes);
    const connectedFolds = this.getConnectedFolds(section.id);
    // Recursively add a group for each child section
    for (let i = 0; i < connectedFolds.length; i++) {
      const fold = connectedFolds[i];
      const childSection = getSection(fold.childSectionId, this.primarySectionSet.sections);
      group.add(this.buildSectionGroup(childSection));
    }
    return group;
  }
  getConnectedFolds(parentId) {
    const folds = [];
    for (let i = 0; i < this.primarySectionSet.folds.length; i++) {
      const fold = this.primarySectionSet.folds[i];
      if (fold.parentSectionId === parentId) {
        folds.push(fold);
      }
    }
    return folds;
  }
  getContentOffset(side) {
    if (side.contentOffset) {
      return new Vector2(side.contentOffset.x, side.contentOffset.y);
    }
    return new Vector2();
  }
  updateLayerOffset(layerOffset) {
    var _a;
    // eslint-disable-next-line no-unused-expressions
    (_a = this.initializedGeometry) === null || _a === void 0 ? void 0 : _a.traverse(obj => {
      if (obj.type === "Mesh") {
        if (obj.uuid.indexOf("Back") >= 0) {
          obj.position.z = -layerOffset;
        } else if (obj.uuid.indexOf("Front") >= 0) {
          obj.position.z = this.primarySectionSet.thickness + layerOffset;
        }
      }
    });
  }
  getGeometry() {
    return __awaiter(this, void 0, void 0, function* () {
      // If existing geometry already exists dispose of it
      if (this.initializedGeometry) {
        disposeGeometryRecursively(this.initializedGeometry);
      }
      const baseSection = getSection(this.primarySectionSet.baseSectionId, this.primarySectionSet.sections);
      // Recursively build all section groups starting with the source
      const sectionGeometry = this.buildSectionGroup(baseSection);
      // Rotate the geometry when a rotation offsets exist
      if (this.primarySectionSet.rotationOffset) {
        const {
          rotationOffset
        } = this.primarySectionSet;
        sectionGeometry.rotation.set(rotationOffset.yaw, rotationOffset.pitch, rotationOffset.roll);
      }
      // Start the product in it's folded state
      yield instantlyPlayAnimation("Folded", this);
      // Find the center of the geometry
      const bounds = new Box3().setFromObject(sectionGeometry);
      const center = bounds.getCenter(new Vector3());
      // Translate the center of the model to the origin
      sectionGeometry.position.set(-center.x, -center.y, -center.z);
      sectionGeometry.updateMatrix();
      // Add the section geometry to a new group to ensure it remains
      // centered during any external animations
      this.initializedGeometry = new Group();
      this.initializedGeometry.add(sectionGeometry);
      return this.initializedGeometry;
    });
  }
  getSurfaceMapping() {
    return this.surfaceMapping;
  }
  setProductSurface(surfaceId, productSurface) {
    if (this.surfaceMapping[surfaceId]) {
      this.surfaceMapping[surfaceId] = productSurface;
    } else {
      throw new Error("'".concat(surfaceId, "' is not a surface on this N-fold product"));
    }
  }
  getSectionGroupMapping() {
    return this.sectionGroupMap;
  }
  getPhysicalSize() {
    return this.physicalSize;
  }
  getCurrentState() {
    return this.currentState;
  }
  setCurrentState(stateId) {
    const newState = this.getStates().find(state => state.id === stateId);
    if (newState) {
      this.currentState = newState;
    } else {
      throw new Error("'".concat(stateId, " is not a state defined in this product!"));
    }
  }
  getStates() {
    return this.productStates;
  }
  setStates(productState) {}
  getAnimationIds() {
    const animationIds = [];
    this.primarySectionSet.foldSequences.forEach(sequence => animationIds.push(sequence.id));
    return animationIds;
  }
  buildAnimation(animationOptions) {
    // check if Fold sequence exist
    const foldSequence = this.primarySectionSet.foldSequences.find(fold => fold.id === animationOptions.animationId);
    if (this.primarySectionSet.foldSequences === undefined || !foldSequence) {
      throw new Error("Fold sequence '".concat(animationOptions.animationId, "' is not an available in the NFold Config!"));
    }
    const options = {
      duration: animationOptions.duration,
      direction: animationOptions.direction,
      sequenceId: animationOptions.animationId
    };
    return new FoldingAnimation(this, this.config, options);
  }
  sync(params) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const zoomFactor = params.preview.camera.normalizedZoomFactor;
    // Compute the updated layer offset using a linear function for zoom factors greater than 1
    const layerOffset = zoomFactor > 1 ? LAYER_OFFSET * 2 * zoomFactor : LAYER_OFFSET;
    // Update to the new layer offset if it changed
    if (Math.abs(this.currentLayerOffset - layerOffset) > EPSILON) {
      this.updateLayerOffset(layerOffset);
      this.currentLayerOffset = layerOffset;
    }
  }
  getAreaLights(pointLightIntensity, directionalLightIntensity) {
    const pointLightIntensities = [0.42 * pointLightIntensity, 0.42 * pointLightIntensity, 0.3885 * pointLightIntensity, 0.3885 * pointLightIntensity, 0.4515 * pointLightIntensity, 0.42 * pointLightIntensity];
    return buildPointLightGrid(pointLightIntensities);
  }
  preLoad() {
    return Promise.resolve();
  }
  dispose() {
    // Nothing to do
    if (!this.initializedGeometry) return;
    disposeGeometryRecursively(this.initializedGeometry);
    this.initializedGeometry = undefined;
  }
}